/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import * as yup from 'yup';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, InputAdornment, Tooltip } from '@material-ui/core';
import { Error } from '@material-ui/icons';
import Button from 'components/ChoreoSystem/Button/Button';
import TextInput from 'components/ChoreoSystem/TextInput/TextInput';
import useStyles from './styles';

interface EnterpriseLoginBoxProps {
  setEnableEnterpriseLogin: Dispatch<SetStateAction<boolean>>;
  handleEnterpriseLoginClick: (userEmail: string) => void;
}

export default function EnterpriseLoginBox(props: EnterpriseLoginBoxProps) {
  const classes = useStyles();
  const intl = useIntl();
  const { setEnableEnterpriseLogin, handleEnterpriseLoginClick } = props;
  const [enterpriseUserEmail, setEnterpriseUserEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>();
  const [displayError, setDisplayError] = useState(false);
  const [inputEmailError, setInputEmailError] = useState('');

  const emailValidationSchema = yup
    .string()
    .email(
      intl.formatMessage({
        id: 'authentication.login.textfield.invalidEmail',
        defaultMessage:
          'Enter a valid email address in the format email@example.com',
      })
    )
    .required(
      intl.formatMessage({
        id: 'authentication.login.textfield.emptyEmail',
        defaultMessage: 'Enter the email address',
      })
    );

  const validateEnterpriseEmail = (email: string) => {
    try {
      emailValidationSchema.validateSync(email, { abortEarly: false });
      setIsEmailValid(true);
      setInputEmailError('');
    } catch (emailError: any) {
      setInputEmailError(emailError.message);
      setIsEmailValid(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userEmail = event.target.value;
    setDisplayError(false);
    validateEnterpriseEmail(userEmail);
    setEnterpriseUserEmail(userEmail);
  };

  const handleContinueClick = () => {
    if (isEmailValid) {
      handleEnterpriseLoginClick(enterpriseUserEmail);
    } else {
      validateEnterpriseEmail(enterpriseUserEmail);
      setDisplayError(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleContinueClick();
    }
  };

  return (
    <Box>
      <Box>
        <TextInput
          autoFocus
          testId="sign-in-with-enterprise"
          placeholder={intl.formatMessage({
            id: 'authentication.login.textfield.enterpriseEmail',
            defaultMessage: 'Enter email',
          })}
          value={enterpriseUserEmail}
          onChange={handleEmailChange}
          onKeyPress={handleKeyPress}
          error={displayError}
          endAdornment={
            displayError && (
              <InputAdornment position="start">
                <Tooltip
                  data-testid="enterprise-email-tooltip"
                  title={inputEmailError}
                  arrow
                >
                  <Error style={{ color: 'red' }} />
                </Tooltip>
              </InputAdornment>
            )
          }
        />
      </Box>
      <Box className={classes.enterpriseLoginBtnWrap}>
        <Button
          testId="sign-in-with-enterprise-continue"
          onClick={handleContinueClick}
          fullWidth
        >
          <FormattedMessage
            id="authentication.login.button.enterpriseContinue"
            defaultMessage="Continue"
          />
        </Button>
      </Box>
      <Box className={classes.goBackLinkWrap}>
        <Button
          testId="sign-in-with-enterprise-back"
          onClick={() => {
            setEnableEnterpriseLogin(false);
          }}
          variant="link"
        >
          <FormattedMessage
            id="authentication.login.button.enterpriseBack"
            defaultMessage="Back"
          />
        </Button>
      </Box>
    </Box>
  );
}
